import {
    partnerCockpitNotificationSchema,
    partnerCockpitNotificationsPageSchema,
    PartnerCockpitNotificationStatusFilterValues,
    partnerCockpitNotificationsUnreadCountSchema,
    type PartnerCockpitNotification,
    type PartnerCockpitNotificationsPage,
    type PartnerCockpitNotificationsQuery,
} from '@houzy/schemas/partner-cockpit/notifications';
import {
    keepPreviousData,
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryOptions,
    type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';

import { apiRequest } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';

export type { PartnerCockpitNotification, PartnerCockpitNotificationsPage };

export const notificationsQueryKey = ['notifications'] as const;

const UNREAD_COUNT_POLL_MS = 60_000; // 60 seconds
const NOTIFICATIONS_STALE_TIME_MS = 30_000; // 30 seconds

const recentNotificationsSchema = z.array(partnerCockpitNotificationSchema);

export const recentNotificationsQueryOptions = queryOptions({
    queryKey: [...notificationsQueryKey, 'recent'],
    queryFn: ({ signal }) =>
        apiRequest(partnerCockpitApiPath('notifications/recent'), {
            signal,
            responseSchema: recentNotificationsSchema,
        }),
    staleTime: NOTIFICATIONS_STALE_TIME_MS,
});

export const unreadNotificationsCountQueryOptions = queryOptions({
    queryKey: [...notificationsQueryKey, 'unreadCount'],
    queryFn: ({ signal }) =>
        apiRequest(partnerCockpitApiPath('notifications/unread-count'), {
            signal,
            responseSchema: partnerCockpitNotificationsUnreadCountSchema,
        }),
    staleTime: NOTIFICATIONS_STALE_TIME_MS,
    refetchInterval: UNREAD_COUNT_POLL_MS,
    select: (data): number => data.count,
});

export const DEFAULT_NOTIFICATIONS_QUERY: PartnerCockpitNotificationsQuery = {
    status: PartnerCockpitNotificationStatusFilterValues.all,
    start: 0,
    pageSize: 10,
};

type NotificationsQueryKey = readonly ['notifications', 'list', PartnerCockpitNotificationsQuery];

export function notificationsQueryOptions(
    query: PartnerCockpitNotificationsQuery,
): UseQueryOptions<PartnerCockpitNotificationsPage, Error, PartnerCockpitNotificationsPage, NotificationsQueryKey> {
    const search = new URLSearchParams({
        status: query.status,
        start: String(query.start),
        pageSize: String(query.pageSize),
    });

    return queryOptions({
        queryKey: ['notifications', 'list', query] as const,
        queryFn: ({ signal }) =>
            apiRequest(partnerCockpitApiPath(`notifications?${search.toString()}`), {
                signal,
                responseSchema: partnerCockpitNotificationsPageSchema,
            }),
        staleTime: NOTIFICATIONS_STALE_TIME_MS,
        // Keeps the current page on screen while switching tab or page
        placeholderData: keepPreviousData,
    });
}

export function useRecentNotifications(enabled: boolean): UseQueryResult<PartnerCockpitNotification[]> {
    return useQuery({ ...recentNotificationsQueryOptions, enabled });
}

export function useUnreadNotificationsCount(): UseQueryResult<number> {
    return useQuery(unreadNotificationsCountQueryOptions);
}

export function useNotifications(
    query: PartnerCockpitNotificationsQuery,
): UseQueryResult<PartnerCockpitNotificationsPage> {
    return useQuery(notificationsQueryOptions(query));
}

export function useMarkNotificationAsRead(): UseMutationResult<void, Error, string> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [...notificationsQueryKey, 'markAsRead'],
        mutationFn: (notificationUuid: string) =>
            apiRequest(partnerCockpitApiPath(`notifications/${notificationUuid}/read`), { method: 'POST' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationsQueryKey }).catch(() => undefined);
        },
    });
}

export function useMarkAllNotificationsAsRead(): UseMutationResult<void, Error, void> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [...notificationsQueryKey, 'markAllAsRead'],
        mutationFn: () => apiRequest(partnerCockpitApiPath('notifications/read-all'), { method: 'POST' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: notificationsQueryKey }).catch(() => undefined);
        },
    });
}
